
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import uniqBy from 'lodash/uniqBy';
import { defineStore } from 'pinia';

import { getPersistingErrorActions } from '@evidentid/dashboard-commons/modules/persisting-error';
import { GatewayApiClient } from '@evidentid/gateway-api-client';
import RpWebApiClient from '@evidentid/rpweb-api-client';
import {
    EntityPatchResult,

} from '@evidentid/rpweb-api-client/models';
import {
    TprmCredentialType,
    TprmRequirementCategory,
    TprmRequirementDefinition,
} from '@evidentid/tprm-portal-lib/models/common';
import { TprmRequestsConfig } from '@evidentid/tprm-portal-lib/models/common/TprmRequestsConfig.model';
import {
    TprmRequirementInstanceModel,
} from '@evidentid/tprm-portal-lib/models/common/TprmRequirementInstanceModel.model';
import { CustomProperty, Entity } from '@evidentid/tprm-portal-lib/models/dashboard';
import {
    CategorizedEnumLabelObject,
    RawCategorizedEnumLabels,
} from '@evidentid/tprm-portal-lib/models/dashboard/CategorizedEnumLabelObject.model';
import { EntityStatistics } from '@evidentid/tprm-portal-lib/models/dashboard/EntityStatistics.model';
import { EntityRequirement } from '@evidentid/tprm-portal-lib/models/entity-details/EntityRequirement.model';
import {
    VerificationRequest,
} from '@evidentid/tprm-portal-lib/models/notification-configuration/VerificationRequest.model';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';
import { EntityFilters } from '@/modules/entity-filtering/types';

export interface DashboardRequirements {
    rpweb: RpWebApiClient;
    gatewayClient: GatewayApiClient;
}

// Actions reach the Application through the $app Pinia plugin; typed here once.
function appOf(store: { $app: any }): DashboardRequirements & {
} {
    return store.$app;
}

const dashboardConfig = {
    recordsPerPage: 25,
};

interface EntitiesListStatus {
    loading: boolean;
    loadingAt?: number;
    finished: boolean;
    count: number;
    list: Entity[];
}

interface CustomPropertiesListStatus {
    status: OperationStatus;
    list: CustomProperty[];
}

// TODO: Refactor to use TprmRequirementType[] as list type
interface RequirementTypeListStatus {
    status: OperationStatus;
    list: string[];
}

interface EntityStatisticsStatus {
    status: OperationStatus;
    statistics: EntityStatistics;
}

interface InsuranceCoveragesStatus {
    status: OperationStatus;
    coverages: EntityRequirement[];
}

interface InsuranceRequestStatus {
    status: OperationStatus;
    requests: VerificationRequest[];
}

interface InsuranceRequestsConfigStatus {
    status: OperationStatus;
    data: TprmRequestsConfig | null;
}

interface InsuranceSubmissionLink {
    status: OperationStatus;
    submissionLink: string;
}

type InsuranceSubmissionLinks = Record<string, InsuranceSubmissionLink>;

export interface RpConfigStatus {
    status: OperationStatus;
    customProperties: CustomProperty[];
    customPropertiesOrder: string[];
    entitiesTableCustomProperties: string[];
    // Whether the relying party still requires one entity per contactEmail (PRODUCT-26139).
    // Absent until a load has succeeded - consumers must check `status` before trusting it rather
    // than reading an absence as either answer, because both are unsafe: enforced hands a migrated
    // relying party a worksheet its own import rejects, non-unique turns every blank Entity ID
    // cell into a duplicate (PRODUCT-26313).
    contactEmailUniquenessEnabled?: boolean;
}

export interface DashboardState {
    entities: Record<string, EntitiesListStatus>;
    customProperties: Record<string, CustomPropertiesListStatus>;
    entityStatistics: Record<string, EntityStatisticsStatus>;
    clientRequirementTypes: Record<string, RequirementTypeListStatus>;
    coverages: Record<string, InsuranceCoveragesStatus>;
    requestsConfig: Record<string, InsuranceRequestsConfigStatus>;
    submissionLinks: Record<string, InsuranceSubmissionLinks>;
    categorizedEnumLabels: CategorizedEnumLabels;
    // same across rp, the String key is requirementName
    tprmRequirementsDefinitions: Record<string, TprmRequirementDefinition>;
    credentialTypes: { status: OperationStatus, list: TprmCredentialType[] };
    requirementInstanceModels: Partial<Record<TprmRequirementCategory, TprmRequirementInstanceModel[]>>;
    lastFilterRequestId: Record<string, number>; // Track latest filter request per RP
    rpConfig: Record<string, RpConfigStatus>;
}

export function createEmptyListStatus(): EntitiesListStatus {
    return {
        count: Infinity,
        finished: false,
        loading: false,
        list: [],
    };
}

export function createEmptyCustomPropertyStatus(): CustomPropertiesListStatus {
    return {
        status: OperationStatus.uninitialized,
        list: [],
    };
}

export function createEmptyClientRequirementTypesStatus(): RequirementTypeListStatus {
    return {
        status: OperationStatus.uninitialized,
        list: [],
    };
}

export function createEmptyEntityStatisticsStatus(): EntityStatisticsStatus {
    return {
        status: OperationStatus.uninitialized,
        statistics: {
            compliance: {
                forcePlaced: 0,
                compliant: 0,
                nonCompliant: 0,
                pending: 0,
                new: 0,
            },
            expiration: {
                within30Days: 0,
            },
        },
    };
}

export function createEmptyCoveragesInfoStatus(): InsuranceCoveragesStatus {
    return {
        status: OperationStatus.uninitialized,
        coverages: [],
    };
}

export function createEmptyRequestsConfigStatus(): InsuranceRequestsConfigStatus {
    return {
        status: OperationStatus.uninitialized,
        data: null,
    };
}

export function createEmptySubmissionLinkStatus(): InsuranceSubmissionLink {
    return {
        status: OperationStatus.uninitialized,
        submissionLink: '',
    };
}

function patchStandardFields(entityToPatch: Entity, patchChanges: EntityPatchResult): void {
    entityToPatch.displayName = patchChanges.displayName?.newValue ?? entityToPatch.displayName;
    entityToPatch.legalName = patchChanges.legalName?.newValue ?? entityToPatch.legalName;
    entityToPatch.doingBusinessAs = patchChanges.doingBusinessAs?.newValue ?? entityToPatch.doingBusinessAs;
    entityToPatch.contactName = patchChanges.contactName?.newValue ?? entityToPatch.contactName;
    entityToPatch.contactEmail = patchChanges.contactEmail?.newValue ?? entityToPatch.contactEmail;
    entityToPatch.contactPhoneNumber = patchChanges.contactPhoneNumber?.newValue ?? entityToPatch.contactPhoneNumber;
    entityToPatch.address = patchChanges.address?.newValue ?? entityToPatch.address;
    entityToPatch.active = patchChanges.active?.newValue ?? entityToPatch.active;
    entityToPatch.paused = patchChanges.paused?.newValue ?? entityToPatch.paused;
    entityToPatch.complianceStatus = patchChanges.complianceStatus?.newValue
        ?? entityToPatch.complianceStatus;
    entityToPatch.nextExpiration = patchChanges.nextExpiration === undefined
        ? entityToPatch.nextExpiration
        : patchChanges.nextExpiration.newValue;
}

function patchCustomProperties(entityToPatch: Entity, patchChanges: EntityPatchResult): void {
    if (patchChanges.insuredFields) {
        Object.entries(patchChanges.insuredFields)
            .forEach(([ k, v ]) => {
                if (!entityToPatch.insuredFields) {
                    entityToPatch.insuredFields = {};
                }
                entityToPatch.insuredFields[k] = v.newValue;
            });
    }
}

type RaceConditionStrategy = 'preventConcurrent' | 'ignoreStale';

interface LoadEntitiesParams {
    context: any;
    rpName: string;
    raceConditionStrategy?: RaceConditionStrategy;
    sort?: string | null;
    filters?: EntityFilters;
    skip?: number;
    loadingAt?: number;
}

const isMutableWithRaceCondition = (
    strategy: RaceConditionStrategy,
    latestStatus: EntitiesListStatus,
    loadingAt: number,
) => {
    // we only mutate when it's still loading status for preventConcurrent strategy
    // loading false => a concurrent job finished and marked before this one, shouldn't mutate result in this case
    const canApplyWithPreventConcurrent = strategy === 'preventConcurrent' && latestStatus?.loading;
    // checks timestamp, each action call will mark and override timestamp, so if matches, it's the latest call
    const canApplyWithIgnoreStale = strategy === 'ignoreStale' && latestStatus?.loadingAt === loadingAt;
    return canApplyWithPreventConcurrent || canApplyWithIgnoreStale;
};

const loadEntitiesWithOptions = async (params: LoadEntitiesParams) => {
    const { context, rpName, sort, filters, skip, raceConditionStrategy = 'preventConcurrent' } = params;
    const currentStatus = context.entities[rpName] || createEmptyListStatus();
    if (raceConditionStrategy === 'preventConcurrent' && currentStatus.loading) {
        return;
    }
    const loadingAt = params.loadingAt || Date.now();
    context.startEntitiesListUpdate({ rpName, loadingAt });

    let pausedQuery = null;
    // TODO(PRODUCT-17634): remove this when paused becomes part of verification status on the data level
    if (filters) {
        if (filters.paused) {
            pausedQuery = { paused: Boolean(filters.paused) };
        } else {
            pausedQuery = filters.verificationStatus ? { paused: false } : null;
        }
    }

    const queryOptions = {
        skip: skip !== undefined ? skip : currentStatus.list.length,
        limit: dashboardConfig.recordsPerPage,
        sort: sort || null,
        ...pausedQuery,
        ...(!(filters?.showDeactivated === 'true') && { active: true }),
        ...(omit(filters, [ 'showDeactivated', 'paused' ])),
    };
    try {
        const { insureds: entities, count } = await appOf(context).gatewayClient.tprmUi()
            .getTprmEntities(rpName, queryOptions);
        const latestStatus = context.entities[rpName];

        if (isMutableWithRaceCondition(raceConditionStrategy, latestStatus, loadingAt)) {
            context.pushToEntitiesList({
                rpName,
                count,
                entities,
                finished: entities.length < queryOptions.limit,
            });
        }
    } catch (error) {
        const latestStatus = context.entities[rpName];
        if (isMutableWithRaceCondition(raceConditionStrategy, latestStatus, loadingAt)) {
            context.pushToEntitiesList({ rpName, entities: [] });
            await getPersistingErrorActions().showError(error);
        }
    }
};

export const useDashboardStore = defineStore('dashboard', {
    state: (): DashboardState => ({
        entities: {},
        customProperties: {},
        entityStatistics: {},
        clientRequirementTypes: {},
        coverages: {},
        requestsConfig: {},
        submissionLinks: {},
        categorizedEnumLabels: {},
        tprmRequirementsDefinitions: {},
        credentialTypes: { status: OperationStatus.uninitialized, list: [] },
        requirementInstanceModels: {},
        lastFilterRequestId: {},
        rpConfig: {},
    }),
    actions: {
        clearEntitiesList(payload: { rpName: string }) {
            this.entities = omit(this.entities, [ payload.rpName ]);
        },

        clearCustomPropertiesList(payload: { rpName: string }) {
        // FIXME: remove this once we have a nav guard that block nav until a component is destoryed
            if (this.customProperties[payload.rpName]?.status === OperationStatus.loading) {
                return;
            }
            this.customProperties = omit(this.customProperties, [ payload.rpName ]);
        },

        clearEntityStatistics(payload: { rpName: string }) {
            this.entityStatistics = omit(this.entityStatistics, [ payload.rpName ]);
        },

        clearClientRequirementTypes(rpName: string) {
            this.clientRequirementTypes = omit(this.clientRequirementTypes, [ rpName ]);
        },

        clearRequestsConfig(payload: { rpName: string }) {
            this.requestsConfig = omit(this.requestsConfig, [ payload.rpName ]);
        },

        clearSubmissionLinks(payload: { rpName: string }) {
            this.submissionLinks = omit(this.submissionLinks, [ payload.rpName ]);
        },

        startEntitiesListUpdate(payload: { rpName: string, loadingAt?: number }) {
            this.entities = {
                ...this.entities,
                [payload.rpName]: {
                    count: Infinity,
                    finished: false,
                    list: [],
                    ...(this.entities[payload.rpName] as EntitiesListStatus | undefined),
                    loading: true,
                    loadingAt: payload.loadingAt || Date.now(),
                },
            } as DashboardState['entities'];
        },

        pushToEntitiesList(payload: {
            rpName: string; entities: Entity[]; count?: number; finished?: boolean;
        }) {
            const prevResult: EntitiesListStatus = this.entities[payload.rpName] || createEmptyListStatus();
            const entitiesCopy = [ ...prevResult.list ];
            payload.entities.forEach(((newEntity) => {
                const index = entitiesCopy.findIndex(
                    (entitiesCopy) => newEntity.id === entitiesCopy.id,
                );
                if (index > -1) {
                    entitiesCopy[index] = newEntity;
                } else {
                    entitiesCopy.push(newEntity);
                }
            }));

            const nextResult: EntitiesListStatus = {
                loading: false,
                loadingAt: prevResult.loadingAt,
                count: payload.count == null ? prevResult.count : payload.count,
                finished: payload.finished == null ? prevResult.finished : payload.finished,
                list: uniqBy(entitiesCopy, 'id'),
            };

            this.entities = {
                ...this.entities,
                [payload.rpName]: nextResult,
            } as DashboardState['entities'];
        },

        updateEntitiesInList(
            payload: { rpName: string, entities: Entity[] }) {
            const prevResult: EntitiesListStatus = this.entities[payload.rpName] || createEmptyListStatus();
            let newEntities = [ ...prevResult.list ];
            payload.entities.forEach((toUpdate) => {
                const index = newEntities.findIndex((x) => x.id === toUpdate.id);
                newEntities = [
                    ...prevResult.list.slice(0, index),
                    toUpdate,
                    ...prevResult.list.slice(index + 1),
                ];
            });
            const nextResult: EntitiesListStatus = {
                loading: false,
                count: prevResult.count,
                finished: prevResult.finished,
                list: uniqBy(newEntities, 'id'),
            };
            this.entities = {
                ...this.entities,
                [payload.rpName]: nextResult,
            } as DashboardState['entities'];
        },

        patchEntitiesInList(payload: { rpName: string, entitiesPatch: EntityPatchResult[] }) {
            payload.entitiesPatch.forEach((patchChanges) => {
                this.entities[payload.rpName].list = this.entities[payload.rpName].list.map((entity) => {
                    if (entity.id !== patchChanges.id) {
                        return entity;
                    }
                    const entityToPatch = { ...entity };
                    patchStandardFields(entityToPatch, patchChanges);
                    patchCustomProperties(entityToPatch, patchChanges);
                    return entityToPatch;
                });
            });
        },

        failEntitiesListUpdate(payload: { rpName: string }) {
            const prevResult: EntitiesListStatus = this.entities[payload.rpName] || createEmptyListStatus();
            const nextResult: EntitiesListStatus = {
                loading: false,
                count: prevResult.count,
                finished: prevResult.finished,
                list: [ ...prevResult.list ],
            };

            this.entities = {
                ...this.entities,
                [payload.rpName]: nextResult,
            } as DashboardState['entities'];
        },

        startLoadingCustomProperties(payload: { rpName: string }) {
            this.customProperties = {
                ...this.customProperties,
                [payload.rpName]: {
                    ...(this.customProperties[payload.rpName] as CustomPropertiesListStatus),
                    status: OperationStatus.loading,
                    list: [],
                },
            };
        },

        finishLoadingCustomProperties(payload: { rpName: string, customProperties: CustomProperty[] }) {
            this.customProperties = {
                ...this.customProperties,
                [payload.rpName]: {
                    ...(this.customProperties[payload.rpName] as CustomPropertiesListStatus),
                    status: OperationStatus.success,
                    list: payload.customProperties,
                },
            };
        },

        failLoadingCustomProperties(payload: { rpName: string }) {
            this.customProperties = {
                ...this.customProperties,
                [payload.rpName]: {
                    ...(this.customProperties[payload.rpName] as CustomPropertiesListStatus),
                    status: OperationStatus.error,
                    list: [],
                },
            };
        },

        startLoadingClientRequirementTypes(payload: { rpName: string }) {
            this.clientRequirementTypes = {
                ...this.clientRequirementTypes,
                [payload.rpName]: {
                    ...(this.clientRequirementTypes[payload.rpName] as RequirementTypeListStatus),
                    status: OperationStatus.loading,
                    list: [],
                },
            };
        },

        finishLoadingClientRequirementTypes(payload: { rpName: string, clientRequirementTypes: string[] }) {
            this.clientRequirementTypes = {
                ...this.clientRequirementTypes,
                [payload.rpName]: {
                    ...(this.clientRequirementTypes[payload.rpName] as RequirementTypeListStatus),
                    status: OperationStatus.success,
                    list: payload.clientRequirementTypes,
                },
            };
        },

        failLoadingClientRequirementTypes(payload: { rpName: string }) {
            this.clientRequirementTypes = {
                ...this.clientRequirementTypes,
                [payload.rpName]: {
                    ...(this.clientRequirementTypes[payload.rpName] as RequirementTypeListStatus),
                    status: OperationStatus.error,
                    list: [],
                },
            };
        },

        startLoadingEntityStatistics(payload: { rpName: string }) {
            this.entityStatistics = {
                ...this.entityStatistics,
                [payload.rpName]: {
                    ...createEmptyEntityStatisticsStatus(),
                    ...(this.entityStatistics[payload.rpName] as EntityStatisticsStatus),
                    status: OperationStatus.loading,
                },
            };
        },

        finishLoadingEntityStatistics(payload: { rpName: string, entityStatistics: EntityStatistics }) {
            this.entityStatistics = {
                ...this.entityStatistics,
                [payload.rpName]: {
                    ...(this.entityStatistics[payload.rpName] as EntityStatisticsStatus),
                    status: OperationStatus.success,
                    statistics: payload.entityStatistics,
                },
            };
        },

        failLoadingEntityStatistics(payload: { rpName: string }) {
            this.entityStatistics = {
                ...this.entityStatistics,
                [payload.rpName]: {
                    ...createEmptyEntityStatisticsStatus(),
                    ...(this.entityStatistics[payload.rpName] as EntityStatisticsStatus),
                    status: OperationStatus.error,
                },
            };
        },

        startLoadingCoveragesInfo(payload: { rpName: string }) {
            this.coverages = {
                ...this.coverages,
                [payload.rpName]: {
                    ...(this.coverages[payload.rpName] as InsuranceCoveragesStatus),
                    status: OperationStatus.loading,
                    coverages: [],
                },
            };
        },

        finishLoadingCoveragesInfo(payload: { rpName: string, coverages: any }) {
            this.coverages = {
                ...this.coverages,
                [payload.rpName]: {
                    ...(this.coverages[payload.rpName] as InsuranceCoveragesStatus),
                    status: OperationStatus.success,
                    coverages: payload.coverages,
                },
            };
        },

        failLoadingCoveragesInfo(payload: { rpName: string }) {
            this.coverages = {
                ...this.coverages,
                [payload.rpName]: {
                    ...(this.coverages[payload.rpName] as InsuranceCoveragesStatus),
                    status: OperationStatus.error,
                    coverages: [],
                },
            };
        },

        startLoadingRequestsConfig(payload: { rpName: string }) {
            this.requestsConfig = {
                ...this.requestsConfig,
                [payload.rpName]: {
                    status: OperationStatus.loading,
                    data: null,
                },
            };
        },

        finishLoadingRequestsConfig(payload: { rpName: string, requestsConfig: TprmRequestsConfig }) {
            this.requestsConfig = {
                ...this.requestsConfig,
                [payload.rpName]: {
                    status: OperationStatus.success,
                    data: payload.requestsConfig,
                },
            };
        },

        failLoadingRequestsConfig(payload: { rpName: string }) {
            this.requestsConfig = {
                ...this.requestsConfig,
                [payload.rpName]: {
                    status: OperationStatus.error,
                    data: null,
                },
            };
        },

        startLoadingSubmissionLink(payload: { rpName: string, entityId: string }) {
            this.submissionLinks = {
                ...this.submissionLinks,
                [payload.rpName]: {
                    ...(this.submissionLinks[payload.rpName] || {}),
                    [payload.entityId]: {
                        status: OperationStatus.loading,
                        submissionLink: '',
                    },
                },
            };
        },

        finishLoadingSubmissionLink(payload: { rpName: string, entityId: string, submissionLink: string }) {
            this.submissionLinks = {
                ...this.submissionLinks,
                [payload.rpName]: {
                    ...(this.submissionLinks[payload.rpName] || {}),
                    [payload.entityId]: {
                        status: OperationStatus.success,
                        submissionLink: payload.submissionLink,
                    },
                },
            };
        },

        failLoadingSubmissionLink(payload: { rpName: string, entityId: string }) {
            this.submissionLinks = {
                ...this.submissionLinks,
                [payload.rpName]: {
                    ...(this.submissionLinks[payload.rpName] || {}),
                    [payload.entityId]: {
                        status: OperationStatus.success,
                        submissionLink: '',
                    },
                },
            };
        },

        updateCategorizedEnumLabels(payload: RawCategorizedEnumLabels) {
            this.categorizedEnumLabels = mapValues(payload,
                (labelObjects) => labelObjects.reduce((acc, labelObj) => ({
                    ...acc,
                    [labelObj.token]: omit(labelObj, 'token'),
                }), {} as Record<string, Omit<CategorizedEnumLabelObject, 'token'>>),
            );
        },

        updateRiskRequirementDefinitions(payload: TprmRequirementDefinition[]) {
            this.tprmRequirementsDefinitions = payload.reduce((acc, requirement) => {
                acc[requirement.name] = requirement;
                return acc;
            }, {} as Record<string, TprmRequirementDefinition>);
        },

        updateRequirementInstanceModels(
            payload: Partial<Record<TprmRequirementCategory, TprmRequirementInstanceModel[]>>,
        ) {
            this.requirementInstanceModels = {
                ...this.requirementInstanceModels,
                ...payload,
            };
        },

        startLoadingCredentialTypes() {
            this.credentialTypes = {
                ...this.credentialTypes,
                status: OperationStatus.loading,
                list: this.credentialTypes.list || [],
            };
        },

        finishLoadingCredentialTypes(credentialTypes: TprmCredentialType[]) {
            this.credentialTypes = {
                ...this.credentialTypes,
                status: OperationStatus.success,
                list: credentialTypes,
            };
        },

        failLoadingCredentialTypes() {
            this.credentialTypes = {
                ...this.credentialTypes,
                status: OperationStatus.error,
                list: this.credentialTypes.list || [],
            };
        },

        updateLastFilterRequestId(payload: { rpName: string, requestId: number }) {
            this.lastFilterRequestId = {
                ...this.lastFilterRequestId,
                [payload.rpName]: payload.requestId,
            };
        },

        startLoadingRpConfig(rpName: string) {
            this.rpConfig = {
                ...this.rpConfig,
                [rpName]: {
                    ...(this.rpConfig[rpName] || {}),
                    status: OperationStatus.loading,
                    customProperties: [],
                    customPropertiesOrder: [],
                    entitiesTableCustomProperties: [],
                },
            };
        },

        finishLoadingRpConfig(payload: {
            rpName: string;
            customProperties: CustomProperty[];
            customPropertiesOrder: string[];
            entitiesTableCustomProperties: string[];
            // Optional so updateRpConfigDisplay, which only reorders columns, can carry whatever the
            // last load left here instead of inventing an answer.
            contactEmailUniquenessEnabled?: boolean;
        }) {
            this.rpConfig = {
                ...this.rpConfig,
                [payload.rpName]: {
                    status: OperationStatus.success,
                    customProperties: payload.customProperties,
                    customPropertiesOrder: payload.customPropertiesOrder,
                    entitiesTableCustomProperties: payload.entitiesTableCustomProperties,
                    contactEmailUniquenessEnabled: payload.contactEmailUniquenessEnabled,
                },
            };
        },

        failLoadingRpConfig(rpName: string) {
            this.rpConfig = {
                ...this.rpConfig,
                [rpName]: {
                    ...(this.rpConfig[rpName] || {}),
                    status: OperationStatus.error,
                    customProperties: [],
                    customPropertiesOrder: [],
                    entitiesTableCustomProperties: [],
                },
            };
        },

        clearRpConfig(payload: { rpName: string }) {
            this.rpConfig = omit(this.rpConfig, [ payload.rpName ]);
        },

        async loadEntities(payload: { rpName: string, sort?: string | null, filters?: EntityFilters }) {
            const { rpName, sort, filters } = payload;
            await loadEntitiesWithOptions({
                context: this, rpName, sort, filters, skip: 0, raceConditionStrategy: 'ignoreStale',
            });
        },
        async loadNextEntities(payload: { rpName: string, sort?: string | null, filters?: EntityFilters }) {
            const { rpName, sort, filters } = payload;
            await loadEntitiesWithOptions({ context: this, rpName, sort, filters });
        },
        async loadEntity(payload: {
            rpName: string;
            id: string;
        }) {
            const { rpName, id } = payload;
            try {
                const entity = await appOf(this).gatewayClient.tprmUi().getTprmEntity(rpName, id);
                this.pushToEntitiesList({
                    rpName,
                    count: 1,
                    entities: [ entity ],
                });
            } catch (error) {
                if (error.reason === 'not-found') {
                    console.error(error);
                    throw error;
                }
                await getPersistingErrorActions().showError(error);
            }
        },
        async loadCustomProperties(payload: {
            rpName: string;
        }) {
            const { rpName } = payload;
            const currentStatus = this.customProperties[rpName] || createEmptyCustomPropertyStatus();
            // Ignore when it is already updating
            if (currentStatus.status === OperationStatus.loading) {
                return;
            }
            this.startLoadingCustomProperties({ rpName });
            try {
                const customProperties = await appOf(this).gatewayClient.tprmUi().getTprmCustomProperties(rpName);
                const latestStatus = this.customProperties[rpName] || createEmptyCustomPropertyStatus();

                // Handle race condition
                if (latestStatus.status !== OperationStatus.loading &&
                    latestStatus.status !== OperationStatus.uninitialized) {
                    return;
                }

                // Process data
                this.finishLoadingCustomProperties({
                    rpName,
                    customProperties,
                });
            } catch (error) {
                this.failLoadingCustomProperties({ rpName });
                await getPersistingErrorActions().showError(error);
            }
        },
        async loadClientRequirementTypes(payload: {
            rpName: string;
        }) {
            const { rpName } = payload;
            const currentStatus = this.coverages[rpName] || createEmptyCoveragesInfoStatus();
            // Ignore when it is already updating
            if (currentStatus.status === OperationStatus.loading) {
                return;
            }
            this.startLoadingClientRequirementTypes({ rpName });
            try {
                const rpAttributesData = await appOf(this).gatewayClient.tprmUi().getRpUsedAttributesData(rpName);
                const clientRequirementTypes = rpAttributesData.coverageTypes
                    .map((requirementTypeAttributes) => requirementTypeAttributes.coverageType);
                const latestStatus =
                    this.clientRequirementTypes[rpName] || createEmptyClientRequirementTypesStatus();

                // Handle race condition
                if (latestStatus.status !== OperationStatus.loading &&
                    latestStatus.status !== OperationStatus.uninitialized) {
                    return;
                }
                // Process data
                this.finishLoadingClientRequirementTypes({ rpName, clientRequirementTypes });
            } catch (error) {
                this.failLoadingClientRequirementTypes({ rpName });
                await getPersistingErrorActions().showError(error);
            }
        },
        async loadEntityStatistics(payload: {
            rpName: string;
        }) {
            const { rpName } = payload;
            const currentStatus = this.entityStatistics[rpName] || createEmptyEntityStatisticsStatus();
            // Ignore when it is already updating
            if (currentStatus.status === OperationStatus.loading) {
                return;
            }
            this.startLoadingEntityStatistics({ rpName });
            try {
                const entityStatistics = await appOf(this).gatewayClient.tprmUi().getTprmEntityStatistics(rpName);
                const latestStatus = this.entityStatistics[rpName] || createEmptyEntityStatisticsStatus();
                // Handle race condition
                if (latestStatus.status !== OperationStatus.loading &&
                    latestStatus.status !== OperationStatus.uninitialized) {
                    return;
                }
                // Process data
                this.finishLoadingEntityStatistics({ rpName, entityStatistics });
            } catch (error) {
                this.failLoadingEntityStatistics({ rpName });
                await getPersistingErrorActions().showError(error);
            }
        },
        async loadRequestsConfig(payload: {
            rpName: string;
        }) {
            const { rpName } = payload;
            const currentStatus = this.requestsConfig[rpName] || createEmptyRequestsConfigStatus();
            // Ignore when it is already updating
            if (currentStatus.status === OperationStatus.loading) {
                return;
            }
            this.startLoadingRequestsConfig({ rpName });
            try {
                const requestsConfig = await appOf(this).gatewayClient.tprmUi().getTprmRequestsConfig(rpName);
                const latestStatus = this.requestsConfig[rpName] || createEmptyRequestsConfigStatus();
                // Handle race condition
                if (latestStatus.status !== OperationStatus.loading &&
                    latestStatus.status !== OperationStatus.uninitialized) {
                    return;
                }
                // Process data
                this.finishLoadingRequestsConfig({ rpName, requestsConfig });
            } catch (error) {
                console.warn('Loading requests config error', error);
                this.failLoadingRequestsConfig({ rpName });
            }
        },
        clearClientRequirementTypeList(payload: { rpName: string }) {
            this.clearClientRequirementTypes(payload.rpName);
        },
        async loadEntitySubmissionLink(payload: {
            rpName: string;
            entityId: string;
        }) {
            const { rpName, entityId } = payload;
            const currentRpStatus = this.submissionLinks[rpName];
            const currentStatus = currentRpStatus && currentRpStatus[entityId]
                ? currentRpStatus[entityId]
                : createEmptySubmissionLinkStatus();
            // Ignore when it is already updating
            if (currentStatus && currentStatus.status === OperationStatus.loading) {
                return;
            }
            this.startLoadingSubmissionLink({ rpName, entityId });
            try {
                const { submissionLink } = await appOf(this).gatewayClient.tprmUi()
                    .getTprmSubmissionLink(rpName, entityId);
                // Process data
                this.finishLoadingSubmissionLink({
                    rpName,
                    entityId,
                    submissionLink: submissionLink || '',
                });
            } catch (error) {
                this.failLoadingSubmissionLink({ rpName, entityId });
                await getPersistingErrorActions().showError(error);
            }
        },
        async loadCategorizedEnumLabels(payload: { rpName: string }) {
            const { rpName } = payload;
            try {
                const enumHumanReadableValues = await appOf(this).gatewayClient.tprmUi()
                    .getCategorizedEnumLabels(rpName);
                this.updateCategorizedEnumLabels(enumHumanReadableValues);
            } catch (error) {
                await getPersistingErrorActions().showError(error);
            }
        },
        async loadRiskRequirementDefinitions() {
            try {
                const requirements = await appOf(this).gatewayClient.tprmUi().getTprmRequirements();
                this.updateRiskRequirementDefinitions(requirements);
            } catch (error) {
                await getPersistingErrorActions().showError(error);
            }
        },
        async loadCredentialTypes() {
            try {
                this.startLoadingCredentialTypes();
                const credentialTypes = await appOf(this).gatewayClient.tprmUi().getTprmCredentialTypes();
                this.finishLoadingCredentialTypes(credentialTypes);
                this.updateRequirementInstanceModels(
                    { [TprmRequirementCategory.credentials]: credentialTypes },
                );
            } catch (error) {
                this.failLoadingCredentialTypes();
                await getPersistingErrorActions().showError(error);
            }
        },
        async loadRpConfig(payload: { rpName: string }) {
            const { rpName } = payload;
            const currentStatus = (this.rpConfig[rpName] as RpConfigStatus | undefined)?.status;
            if (currentStatus === OperationStatus.loading) {
                return;
            }
            this.startLoadingRpConfig(rpName);
            try {
                const rawConfig = await appOf(this).gatewayClient.tprmUi().getRpConfiguration(rpName);
                const latestStatus = (this.rpConfig[rpName] as RpConfigStatus | undefined)?.status;
                if (latestStatus !== OperationStatus.loading) {
                    return; // race condition: a concurrent call already completed
                }
                const idToKey = new Map(rawConfig.customProperties.map((p) => [ p.id, p.key ]));
                this.finishLoadingRpConfig({
                    rpName,
                    customProperties: rawConfig.customProperties,
                    customPropertiesOrder: rawConfig.customPropertiesOrder
                        .map((id) => idToKey.get(id) ?? id),
                    entitiesTableCustomProperties: rawConfig.entitiesTableCustomProperties
                        .map((id) => idToKey.get(id) ?? id),
                    // An API that predates the field is an API whose relying parties all enforce
                    // uniqueness, so absent reads as true.
                    contactEmailUniquenessEnabled: rawConfig.contactEmailUniquenessEnabled ?? true,
                });
            } catch (error) {
                this.failLoadingRpConfig(rpName);
                await getPersistingErrorActions().showError(error);
            }
        },
        updateRpConfigDisplay(payload: {
            rpName: string;
            customPropertiesOrder: string[];
            entitiesTableCustomProperties: string[];
        }) {
            const current = this.rpConfig[payload.rpName];
            if (!current) {
                return;
            }
            this.finishLoadingRpConfig({
                rpName: payload.rpName,
                customProperties: current.customProperties,
                customPropertiesOrder: payload.customPropertiesOrder,
                entitiesTableCustomProperties: payload.entitiesTableCustomProperties,
                contactEmailUniquenessEnabled: current.contactEmailUniquenessEnabled,
            });
        },
    },
});

// Compat shim: legacy callers built a dispatch-map via getActions(scope); the
// Pinia store carries all action methods itself, so just return it.
export const getDashboardActions = (_scope?: unknown) => useDashboardStore();
